/**
 * The attribute, the copy in `localStorage`, and the thing that watches the
 * device.
 *
 * The rule about *how much motion* somebody gets is not here — it is in
 * `src/lib/motion.ts`, which the server imports too. What is here is what the
 * browser then does with the answer: one attribute on `<html>`, one key in
 * storage, and one subscription so that a screen re-draws when either half of
 * the question changes underneath it.
 *
 * **No DOM.** Everything below takes the element, the storage and the media
 * query it works on as arguments, typed structurally, for the same reason
 * `src/theme.ts` does: what is worth testing about a motion preference is that
 * the right attribute lands on the root and that flipping the device's own
 * setting is noticed, and standing up a whole DOM implementation to watch that
 * happen is the slower way to find out less. `src/motion-document.ts` is the
 * handful of lines that reach for the real `document`, the real
 * `localStorage` and the real `matchMedia`.
 *
 * ## Why there is a copy in localStorage at all
 *
 * The same reason the theme has one, and a sharper one. The preference lives
 * on the user row; that is the record. But the row is behind a request, and
 * the first list of rows on the screen has *already staggered in* by the time
 * a request comes back — the one animation a late answer cannot un-run. So
 * the resolved state is written to storage, the inline script in `index.html`
 * reads it before the first paint, and somebody who asked for a calm app gets
 * one from the first frame rather than from the second.
 *
 * It holds the **resolved** state, `full` or `reduced`, never the preference.
 * Storage exists to answer "was this screen moving last time", and `system`
 * is not an answer to that. If the device's setting changed since the last
 * load the stored answer is stale for one paint, and the media query below
 * corrects it as soon as this module runs — the same trade the theme makes,
 * and the same one it is worth.
 */

import {
  DEFAULT_MOTION_PREFERENCE,
  MOTION_ATTRIBUTE,
  MOTION_STORAGE_KEY,
  isMotionState,
  resolveMotion,
  type MotionPreference,
  type MotionState,
} from './lib/motion'

/** The two methods of `localStorage` this uses, and null for "there isn't one". */
export type MotionStorage = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
} | null

/** The two things this does to `<html>`, and nothing else. */
export type MotionRoot = {
  getAttribute(name: string): string | null
  setAttribute(name: string, value: string): void
}

/**
 * As much of `MediaQueryList` as this needs. The listener pair is optional
 * because a browser that cannot answer the question at all still has to get a
 * working app — it simply gets one that never hears the setting change.
 */
export type MotionMedia = {
  matches: boolean
  addEventListener?(type: 'change', listener: () => void): void
  removeEventListener?(type: 'change', listener: () => void): void
} | null

/** The query the whole device half of this rests on. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/** What the last load settled on, if it is still a thing this app understands. */
export function readStoredMotion(storage: MotionStorage): MotionState | null {
  try {
    const stored = storage?.getItem(MOTION_STORAGE_KEY) ?? null
    return isMotionState(stored) ? stored : null
  } catch {
    return null
  }
}

/**
 * Remembers the resolved state for the next first paint. Storage being full,
 * disabled or blocked costs the next load its first stagger and nothing else,
 * so it is swallowed rather than surfaced.
 */
export function rememberMotion(state: MotionState, storage: MotionStorage): void {
  if (!isMotionState(state)) return
  try {
    storage?.setItem(MOTION_STORAGE_KEY, state)
  } catch {
    /* not worth telling anybody about */
  }
}

/** What is on the element now — the state the current paint is using. */
export function currentMotion(root: MotionRoot): MotionState | null {
  const value = root.getAttribute(MOTION_ATTRIBUTE)
  return isMotionState(value) ? value : null
}

/**
 * Puts `state` on the root. Always both values, never "absent means full":
 * the stylesheet has to be able to tell "this device reduces motion and
 * nobody has said otherwise" from "this account asked for movement anyway",
 * and only an explicit `data-motion="full"` can say the second one.
 */
export function applyMotion(root: MotionRoot, state: MotionState): void {
  if (!isMotionState(state)) return
  if (currentMotion(root) === state) return
  root.setAttribute(MOTION_ATTRIBUTE, state)
}

/**
 * The live answer: what the app is doing now, what was asked for, and a way
 * to be told when either changes.
 *
 * A tiny store rather than React state because both of its inputs arrive from
 * outside React — the session, and the operating system — and because
 * `useSyncExternalStore` in `src/motion-react.ts` wants exactly this shape.
 * `state()` returns one of two strings, so it is its own snapshot and nothing
 * has to be memoised for React to leave a component alone.
 */
export type MotionController = {
  state(): MotionState
  preference(): MotionPreference
  /** The account's choice has arrived, or been changed. Applies and remembers. */
  set(preference: MotionPreference): void
  subscribe(listener: () => void): () => void
  /** Stops listening to the device. Only the tests ever need this. */
  stop(): void
}

export function createMotionController(deps: {
  root: MotionRoot
  storage?: MotionStorage
  media?: MotionMedia
}): MotionController {
  const { root, storage = null, media = null } = deps

  let preference: MotionPreference = DEFAULT_MOTION_PREFERENCE
  const listeners = new Set<() => void>()

  /**
   * The stored state is the starting point only until the device has been
   * asked — which is immediately, right below. It exists for the paint that
   * happened before any of this ran.
   */
  let state: MotionState = readStoredMotion(storage) ?? 'full'

  function settle(): void {
    const next = resolveMotion(preference, media?.matches ?? false)
    applyMotion(root, next)
    rememberMotion(next, storage)
    if (next === state) return
    state = next
    for (const listener of [...listeners]) listener()
  }

  const onDeviceChange = () => settle()
  media?.addEventListener?.('change', onDeviceChange)
  settle()

  return {
    state: () => state,
    preference: () => preference,
    set(next: MotionPreference) {
      preference = next
      settle()
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    stop() {
      media?.removeEventListener?.('change', onDeviceChange)
      listeners.clear()
    },
  }
}
